import React from 'react';
import styled, { ThemeContext } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Button } from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import Can, { AbilityContext } from '../../context/ability';
import {
  GUIDELINES_PERMISSIONS,
  PERMISSION_ENTITIES,
} from '../../helpers/permissions';
import {
  BackButtonBehavior,
  EntityVisibility,
  GuideType,
  PermissionType,
} from '../../types';
import {
  StyledHeader,
  StyledHeaderContainer,
  StyledHeaderInfo,
  StyledPublishContainer,
} from '../shared/styled-components/Header';
import PrimaryButton from '../shared/PrimaryButton';
import HeaderSubtitle from './HeaderSubtitle';
import { useActiveGuide, useActivePage } from '../../queries/guides';
import { ROUTES } from '../../constants/app';
import { UserType, useUserType } from '../../helpers/hooks';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { size } from '../../constants/styles';
import { InspectorSetting } from '../../constants/guides';
import StateIcon from '../shared/StateIcon';
import { LogoContainer, Logo } from '../themesets/Styles';
import { useGuideStore } from '../../stores/guideStore';
import { useInspectorStore } from '../../stores/inspectorStore';
import { IconArrowLeft, IconEdit, IconSettings, IconShow, IconUserGroup } from '@bynder/icons';
import { useThemesetByGuideId } from '../../queries/themesets';
import { useNavigation } from '../../hooks/useNavigation';
import { OverviewSections, useOverviewStore } from '../../stores/overviewStore';
import { GuideShareLinkButton } from './GuideShareLinkButton';
import { ThemeContextType } from '../themesets/ThemesetManager';
import { useAudiencesModal } from '../../hooks/useModals';

interface Props {
  guideId: string;
  isGuideShared: boolean;
  guideVisibility: EntityVisibility;
  canEditGuide: boolean;
  guideGroupId: string | null;
  guideTitle: string;
  guideOwnerId: string;
  guideType: GuideType;
}

const Header: React.FC<Props> = ({
  guideId,
  canEditGuide,
  isGuideShared,
  guideGroupId,
  guideVisibility,
  guideTitle,
  guideOwnerId,
  guideType,
}) => {
  const { getValueOrDefault } = React.useContext(
    ThemeContext,
  ) as ThemeContextType;
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { data: activePage } = useActivePage();
  const { translate } = useTranslate();
  const isGuidePublic = guideVisibility === EntityVisibility.PUBLIC;
  const isCustomLogoEnabled = getValueOrDefault('properties.customLogoEnabled');
  const logoUrl = getValueOrDefault('properties.logoUrl');
  const { userType } = useUserType();
  const hasLogoEnabled =
    isGuidePublic && userType === UserType.PUBLIC && isCustomLogoEnabled;
  const { width } = useWindowDimensions();

  const renderUserActions = () => {
    if (isEditMode) {
      return (
        <StyledPublishContainer gap="4">
          {canEditGuide && <ToggleInspectorPanelButton />}
          {activePage?.canEdit && <SwitchPreviewModeButton />}
        </StyledPublishContainer>
      );
    }

    return (
      <StyledPublishContainer gap="4">
        <GuideShareLinkButton
          guideId={guideId}
          buttonText={translate('HEADER.SHARE_GUIDE_BUTTON_TEXT')}
          color={getValueOrDefault('navigationBar.textColor.hexCode') as string}
        />

        {activePage?.canEdit && width > size.tablet && (
          <SwitchPreviewModeButton />
        )}
      </StyledPublishContainer>
    );
  };

  return (
    <StyledHeader data-testid="guideHeader">
      {hasLogoEnabled ? (
        <LogoContainer>
          <Logo src={logoUrl as string} />
        </LogoContainer>
      ) : (
        <BackButton
          guideId={guideId}
          guideGroupId={guideGroupId}
          guideType={guideType}
        />
      )}
      <StyledHeaderContainer $isEditMode={isEditMode}>
        {isEditMode && (
          <StateIcon
            isShared={isGuideShared}
            entityVisibility={guideVisibility}
            entityId={guideId}
            hasTooltip
            entityType={PermissionType.Guides}
          />
        )}
        <StyledHeaderInfo>
          <h1 data-testid="guideTitle">{guideTitle}</h1>
          {isEditMode && <HeaderSubtitle authorId={guideOwnerId} />}
        </StyledHeaderInfo>
      </StyledHeaderContainer>
      <StyledUserActions>{renderUserActions()}</StyledUserActions>
    </StyledHeader>
  );
};

export default Header;

const StyledUserActions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

export const SwitchPreviewModeButton = () => {
  const { width } = useWindowDimensions();
  const isEditMode = useGuideStore(s => s.isEditMode);

  if (width < size.laptop) {
    return null;
  }

  return isEditMode ? <ViewModeButton /> : <EditModeButton />;
};

export const BackButton: React.FC<{
  guideId: string;
  guideGroupId: string | null;
  guideType: GuideType;
}> = ({ guideId, guideGroupId, guideType }) => {
  const abilities = React.useContext(AbilityContext);
  const history = useHistory();
  const { data: themeset } = useThemesetByGuideId(guideId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { navigate } = useNavigation();
  const activeSection = useOverviewStore.use.activeSection();

  const isBackButtonHidden = !themeset?.properties?.backButton;

  const canViewGroups = abilities.can(
    GUIDELINES_PERMISSIONS.VIEW,
    PERMISSION_ENTITIES.GROUP,
  );

  const navigateBack = () => {
    const navigateToOverview = () => {
      const isComingFrom = {
        guides: activeSection === OverviewSections.GUIDES,
        pages: activeSection === OverviewSections.LANDING_PAGES,
        homepages: activeSection === OverviewSections.HOMEPAGES,
        groupsOverview: activeSection === OverviewSections.GROUPS,
      };

      if (activeSection) {
        if (isComingFrom.guides) {
          history.push(ROUTES.GUIDES_OVERVIEW);
        } else if (isComingFrom.pages) {
          history.push(ROUTES.PAGES_OVERVIEW);
        } else if (isComingFrom.homepages) {
          history.push(ROUTES.HOMEPAGES_OVERVIEW);
        } else if (isComingFrom.groupsOverview && guideGroupId) {
          history.push(ROUTES.GROUP_OVERVIEW(guideGroupId));
        } else {
          history.push(ROUTES.ALL_OVERVIEW);
        }
        return;
      }

      if (guideGroupId && canViewGroups) {
        history.push(ROUTES.GROUP_OVERVIEW(guideGroupId));
      } else if (guideType === GuideType.SINGLE_PAGE) {
        history.push(ROUTES.PAGES_OVERVIEW);
      } else {
        history.push(ROUTES.GUIDE_OVERVIEW);
      }
    };

    if (isEditMode) {
      navigateToOverview();
    } else {
      switch (themeset?.properties.backButtonBehavior.behavior) {
        case BackButtonBehavior.BROWSER:
          history.goBack();
          break;
        case BackButtonBehavior.CUSTOM:
          if (themeset && themeset.properties.backButtonBehavior.link) {
            navigate(themeset?.properties.backButtonBehavior.link, false);
          }
          break;
        case BackButtonBehavior.OVERVIEW:
        default:
          navigateToOverview();
      }
    }
  };

  if (isBackButtonHidden) return null;

  return (
    <Can I={GUIDELINES_PERMISSIONS.VIEW} a={PERMISSION_ENTITIES.GUIDE}>
      <Button
        variant="clean"
        icon={<IconArrowLeft aria-label="keyboard_arrow_left" />}
        onClick={navigateBack}
        data-testid="headerBackButton"
        aria-label="Back"
      />
    </Can>
  );
};

export const ToggleInspectorPanelButton = () => {
  const setIsInspectorOpen = useInspectorStore(s => s.setIsInspectorOpen);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const { translate } = useTranslate();
  return (
    <Button
      icon={<IconSettings />}
      variant="secondary"
      data-testid="inspectorButton"
      title="Settings"
      onClick={() => {
        setIsInspectorOpen(true);
        setActiveInspectorSetting(InspectorSetting.GUIDE_VIEW);
      }}
    >
      {translate('PAGE_HEADER.TOGGLE_INSPECTOR_PANEL_BUTTON_TEXT')}
    </Button>
  );
};

export const ToggleAudiencesModalButton = () => {
  const openAudiencesModal = useAudiencesModal();

  return (
    <Button
      variant="secondary"
      icon={<IconUserGroup />}
      onClick={openAudiencesModal}
    >
      <Translate id="AUDIENCES_MODAL.TOGGLE_BUTTON" />
    </Button>
  );
};

export const EditModeButton = () => {
  const { data: guide } = useActiveGuide();
  const isMultiPage = guide?.type === GuideType.MULTI_PAGE;
  const setIsEditMode = useGuideStore(s => s.setIsEditMode);
  const setActiveSectionId = useGuideStore(s => s.setActiveSectionId);
  const setIsInspectorOpen = useInspectorStore(s => s.setIsInspectorOpen);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);
  const onChangeViewMode = () => {
    setIsInspectorOpen(true);
    setIsEditMode(true);
    setWidgetSettings(null);
    setActiveInspectorSetting(InspectorSetting.GUIDE_VIEW);
    setActiveSectionId(null);
  };
  return (
    <PrimaryButton
      onClick={onChangeViewMode}
      data-testid="editGuideButton"
      icon={<IconEdit />}
    >
      <Translate
        id={
          isMultiPage
            ? 'HEADER.EDIT_GUIDE_BUTTON_TEXT'
            : 'HEADER.EDIT_GUIDE_BUTTON_TEXT_PAGE'
        }
      />
    </PrimaryButton>
  );
};

const ViewModeButton = () => {
  const { data: guide } = useActiveGuide();
  const setIsEditMode = useGuideStore(s => s.setIsEditMode);
  const setActiveSectionId = useGuideStore(s => s.setActiveSectionId);
  const setIsInspectorOpen = useInspectorStore(s => s.setIsInspectorOpen);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);
  const onChangeViewMode = () => {
    setIsInspectorOpen(false);
    setIsEditMode(false);
    setWidgetSettings(null);
    setActiveInspectorSetting(null);
    setActiveSectionId(null);
  };

  const isMultiPage = guide?.type === GuideType.MULTI_PAGE;
  return (
    <Button
      icon={<IconShow />}
      onClick={onChangeViewMode}
      data-testid="previewButton"
      variant="secondary"
    >
      <Translate
        id={
          isMultiPage
            ? 'HEADER.VIEW_MODE_SWITCH_TEXT'
            : 'HEADER.VIEW_MODE_SWITCH_TEXT_PAGE'
        }
      />
    </Button>
  );
};
